import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";

import { api } from "./ApiClient";

import { AuthHelper } from "../helpers/auth.helper";

import { closeModal } from "../store/modal.slice";

import { HMIDeleteSubClient, HMISaveSubClient, ISubClientListItem, ISubClientUserListItem, IUsersListItemForSubClientSelection, HMIAddUserToSubClient, HMIRemoveUserFromSubClient, HMISaveSubClientUserEntitlements } from "../types/services.interfaces/sub_clients-cax";

export const useGetSubClients = () => {
    AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<ISubClientListItem[]> => {
        const res = await api.get(`/sub-clients-cax/list`);
        return res.data;
    };

    return useQuery({
        queryKey: ['sub-clients-list'],
        queryFn,
    });
};

export const useGetSubClient = (subClientId: number | null) => {
    AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<ISubClientListItem> => {
        const res = await api.get(`/sub-clients-cax/details/${subClientId}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['sub-clients-details', subClientId],
        enabled: !!subClientId,
        queryFn,
    });
};

export const useSaveSubClient = () => {
    AuthHelper.presetPrivateRequest(api);

    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const mutationFn = async (data: HMISaveSubClient): Promise<void> => {
        const res = await api.post('/sub-clients-cax/save', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: () => {
            toast.success('Sub client saved successfully', {
                duration: 4000,
                position: 'top-right',
            });

            dispatch(closeModal("MANAGE_SUB_CLIENT_MODAL"));
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 4000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['sub-clients-list'] });
            variables.setIsProcessing(false);
        },
    });
};

export const useDeleteSubClient = () => {
    AuthHelper.presetPrivateRequest(api);

    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIDeleteSubClient): Promise<void> => {
        const res = await api.delete(`/sub-clients-cax/delete/${data.id}`);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: () => {
            toast.success('Sub client deleted successfully', {
                duration: 4000,
                position: 'top-right',
            });
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 4000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['sub-clients-list'] });
            variables.setIsProcessing(false);
        },
    });
};

export const useGetSubClientUsers = (subClientId: number | null) => {
    AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<ISubClientUserListItem[]> => {
        const res = await api.get(`/sub-clients-cax/sub-client-users/${subClientId}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['sub-client-users', subClientId],
        enabled: !!subClientId,
        queryFn,
    });
};

export const useGetUsersListForSubClientUserSelection = (subClientId: number | null) => {
    AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<IUsersListItemForSubClientSelection[]> => {
        const res = await api.get(`/sub-clients-cax/users-list-for-sub-client-user-selection/${subClientId}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['users-list-for-sub-client-user-selection', subClientId],
        enabled: !!subClientId,
        queryFn,
    });
};

export const useAddUserToSubClient = () => {
    AuthHelper.presetPrivateRequest(api);

    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIAddUserToSubClient): Promise<void> => {
        const res = await api.put(`/sub-clients-cax/add-user/${data.userId}/${data.subClientId}`);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: () => {
            toast.success('User added to sub client successfully', {
                duration: 4000,
                position: 'top-right',
            });
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 4000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['sub-client-users'] });
            queryClient.invalidateQueries({ queryKey: ['users-list-for-sub-client-user-selection'] });
            variables.setIsProcessing(false);
        },
    });
};

export const useRemoveUserFromSubClient = () => {
    AuthHelper.presetPrivateRequest(api);

    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIRemoveUserFromSubClient): Promise<void> => {
        const res = await api.put(`/sub-clients-cax/remove-user/${data.userId}/${data.subClientId}`);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: () => {
            toast.success('User removed from sub client successfully', {
                duration: 4000,
                position: 'top-right',
            });
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 4000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['sub-client-users'] });
            queryClient.invalidateQueries({ queryKey: ['users-list-for-sub-client-user-selection'] });
            variables.setIsProcessing(false);
        },
    });
};

export const useSaveSubClientUserEntitlements = () => {
    AuthHelper.presetPrivateRequest(api);

    const queryClient = useQueryClient();

    const mutationFn = async (data: HMISaveSubClientUserEntitlements): Promise<void> => {
        const res = await api.post(`/sub-clients-cax/save-sub-client-user-entitlements/${data.userId}/${data.subClientId}`, data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setProcessingUserId(variables.userId);
        },
        onSuccess: () => {
            toast.success('User entitlements saved successfully', {
                duration: 4000,
                position: 'top-right',
            });
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 4000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['sub-client-users'] });
            queryClient.invalidateQueries({ queryKey: ['sub-clients-list'] });
            queryClient.invalidateQueries({ queryKey: ['sub-clients-details'] });
            queryClient.invalidateQueries({ queryKey: ['entitlements-for-admin-form'] });
            variables.setProcessingUserId(null);
        },
    });
};